import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Map,
  BarChart3,
  CalendarDays,
  Factory,
  FireExtinguisher,
  Wrench,
  CircleDollarSign,
  HardHat,
  Ruler,
  Settings,
  Database,
  Flame,
  Sparkles,
  X,
  RefreshCw,
  CheckCheck,
  Building2,
  ShieldCheck,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useT } from '@/lib/i18n';
import type { I18nKey } from '@/lib/i18n';
import { motion, AnimatePresence } from 'motion/react';
import { useApp } from '@/store';
import { useEffect, useState } from 'react';
import { InstallBanner } from '@/components/InstallBanner';
import { useSWStatus, SWPhase } from '@/lib/useSWStatus';
import { getDecoded, onChange as onAuthChange } from '@/lib/auth';

type Item = {
  to: string;
  icon: typeof LayoutDashboard;
  labelKey: I18nKey;
  badgeKey?:
    | 'customers'
    | 'extinguishers'
    | 'hoses'
    | 'manufacturers'
    | 'workers';
};

const mainItems: Item[] = [
  {
    to: '/overview',
    icon: LayoutDashboard,
    labelKey: 'nav.overview',
    badgeKey: 'customers',
  },
  { to: '/map', icon: Map, labelKey: 'nav.map' },
  { to: '/reports', icon: BarChart3, labelKey: 'nav.reports' },
  { to: '/calendar', icon: CalendarDays, labelKey: 'nav.calendar' },
];
const adminItems: Item[] = [
  {
    to: '/admin/manufacturers',
    icon: Factory,
    labelKey: 'nav.manufacturers',
    badgeKey: 'manufacturers',
  },
  {
    to: '/admin/device-types',
    icon: FireExtinguisher,
    labelKey: 'nav.deviceTypes',
  },
  { to: '/admin/spare-parts', icon: Wrench, labelKey: 'nav.spareParts' },
  {
    to: '/admin/service-prices',
    icon: CircleDollarSign,
    labelKey: 'nav.servicePrices',
  },
  {
    to: '/admin/workers',
    icon: HardHat,
    labelKey: 'nav.workers',
    badgeKey: 'workers',
  },
  { to: '/admin/hose-options', icon: Ruler, labelKey: 'nav.hoseOptions' },
  { to: '/admin/settings', icon: Settings, labelKey: 'nav.settings' },
];
const dataItems: Item[] = [
  { to: '/import-export', icon: Database, labelKey: 'nav.importExport' },
];

const superAdminItems: Item[] = [
  { to: '/superadmin/tenants', icon: Building2, labelKey: 'nav.tenants' },
  { to: '/superadmin/audit', icon: ShieldCheck, labelKey: 'nav.platformAudit' },
];

function useBadge(key?: Item['badgeKey']): number | null {
  const customers = useApp(s => s.customers);
  const manufacturers = useApp(s => s.manufacturers);
  const workers = useApp(s => s.workers);
  if (!key) return null;
  if (key === 'customers') return customers.length;
  if (key === 'extinguishers')
    return customers.reduce((n, c) => n + c.extinguishers.length, 0);
  if (key === 'hoses') return customers.reduce((n, c) => n + c.hoses.length, 0);
  if (key === 'manufacturers') return manufacturers.length;
  if (key === 'workers') return workers.filter(w => w.active).length;
  return null;
}

function NavItem({ it, index }: { it: Item; index: number }) {
  const t = useT();
  const Icon = it.icon;
  const badge = useBadge(it.badgeKey);
  const setSidebarOpen = useApp(s => s.setSidebarOpen);
  return (
    <motion.div
      initial={{ opacity: 0, x: -8 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.25, delay: 0.03 * index }}
    >
      <NavLink
        to={it.to}
        onClick={() => setSidebarOpen(false)}
        className={({ isActive }) =>
          cn(
            'group relative flex items-center gap-3 rounded-lg px-3 py-3 sm:py-2 text-sm transition-all duration-200',
            isActive
              ? 'bg-gradient-to-r from-primary/15 via-primary/10 to-transparent text-primary font-semibold shadow-sm'
              : 'text-foreground/75 hover:bg-accent/70 hover:text-foreground hover:translate-x-0.5'
          )
        }
      >
        {({ isActive }) => (
          <>
            <span
              className={cn(
                'absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r-full transition-all',
                isActive
                  ? 'flame-gradient opacity-100 scale-y-100'
                  : 'opacity-0 scale-y-50'
              )}
            />
            <Icon
              className={cn(
                'h-4 w-4 shrink-0 transition-colors',
                isActive
                  ? 'text-primary'
                  : 'text-muted-foreground group-hover:text-foreground'
              )}
            />
            <span className="flex-1 truncate">{t(it.labelKey)}</span>
            {badge !== null && badge > 0 && (
              <span
                className={cn(
                  'inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[10px] font-semibold tabular-nums transition-colors',
                  isActive
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground group-hover:bg-foreground/10'
                )}
              >
                {badge}
              </span>
            )}
          </>
        )}
      </NavLink>
    </motion.div>
  );
}

function Group({
  titleKey,
  items,
  startIndex,
}: {
  titleKey: I18nKey;
  items: Item[];
  startIndex: number;
}) {
  const t = useT();
  return (
    <div className="mb-5">
      <div className="px-3 text-[10px] font-bold uppercase tracking-[0.12em] text-muted-foreground/70 mb-2 flex items-center gap-2">
        <span>{t(titleKey)}</span>
        <div className="h-px flex-1 bg-gradient-to-r from-border to-transparent" />
      </div>
      <div className="space-y-0.5">
        {items.map((it, i) => (
          <NavItem key={it.to} it={it} index={startIndex + i} />
        ))}
      </div>
    </div>
  );
}

function SidebarFooter() {
  const t = useT();
  const online = useApp(s => s.online);
  const { phase } = useSWStatus();
  const [checking, setChecking] = useState(false);
  const [upToDate, setUpToDate] = useState(false);

  const isWorking = phase === SWPhase.CACHING || phase === SWPhase.LOADING;
  const isUpdating = phase === SWPhase.UPDATING;

  // Clear checking state when update is found
  useEffect(() => {
    if (isUpdating) {
      setChecking(false);
      setUpToDate(false);
    }
  }, [isUpdating]);

  const handleCheckUpdate = () => {
    if (checking || isUpdating || isWorking || !online) return;
    setChecking(true);
    setUpToDate(false);
    navigator.serviceWorker.ready
      .then(r => r.update())
      .then(() => {
        // If updatefound fires, isUpdating → true → useEffect clears checking.
        // Otherwise after 3s assume no update.
        setTimeout(() => {
          setChecking(prev => {
            if (prev) {
              setUpToDate(true);
              setTimeout(() => setUpToDate(false), 2500);
            }
            return false;
          });
        }, 3000);
      })
      .catch(() => setChecking(false));
  };

  const canCheck =
    online &&
    phase === SWPhase.READY &&
    !isWorking &&
    !isUpdating &&
    'serviceWorker' in navigator;
  const dotColor = !online
    ? 'bg-red-500'
    : isUpdating
      ? 'bg-amber-500'
      : isWorking
        ? 'bg-blue-500'
        : 'bg-emerald-500';
  const label = !online
    ? t('sidebar.offlineMode')
    : isUpdating
      ? t('sidebar.updating')
      : isWorking
        ? t('sidebar.caching')
        : phase === SWPhase.READY
          ? t('sidebar.offlineReady')
          : t('sidebar.systemReady');

  return (
    <div className="border-t bg-muted/20 p-3 space-y-1.5">
      <div className="flex items-center justify-between text-[11px]">
        <div className="flex items-center gap-1.5">
          <span className={cn('relative flex h-2 w-2 rounded-full', dotColor)}>
            {online && !isWorking && !isUpdating && (
              <span className="absolute inset-0 rounded-full bg-emerald-500 animate-ping opacity-60" />
            )}
            {isUpdating && (
              <span className="absolute inset-0 rounded-full bg-amber-500 animate-ping opacity-60" />
            )}
            {isWorking && (
              <span className="absolute inset-0 rounded-full bg-blue-500 animate-ping opacity-60" />
            )}
          </span>
          <span className="font-medium text-muted-foreground">{label}</span>
        </div>
        <span className="text-muted-foreground/60 font-mono">
          v{__APP_VERSION__}
        </span>
      </div>
      <AnimatePresence>
        {(isWorking || isUpdating) && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
          >
            <div className="h-1 rounded-full bg-muted overflow-hidden">
              <motion.div
                className={`h-full w-1/3 rounded-full ${isUpdating ? 'bg-amber-500' : 'bg-blue-500'}`}
                animate={{ x: ['0%', '200%'] }}
                transition={{
                  duration: 1.2,
                  repeat: Infinity,
                  ease: 'easeInOut',
                }}
              />
            </div>
          </motion.div>
        )}
        {canCheck && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
          >
            {upToDate ? (
              <div className="flex items-center gap-1.5 text-[10px] text-emerald-600 dark:text-emerald-400 font-medium">
                <CheckCheck className="h-3 w-3" />
                {t('sidebar.upToDate')}
              </div>
            ) : (
              <button
                type="button"
                onClick={handleCheckUpdate}
                disabled={checking}
                data-focus="check-update-btn"
                className="flex items-center gap-1.5 text-[10px] text-muted-foreground/70 hover:text-primary transition-colors disabled:opacity-50"
              >
                <RefreshCw
                  className={`h-3 w-3 ${checking ? 'animate-spin' : ''}`}
                />
                {checking ? t('sidebar.checking') : t('sidebar.checkUpdate')}
              </button>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function useRoleSnapshot() {
  const [snap, setSnap] = useState(() => {
    const d = getDecoded();
    return {
      roles: d?.roles ?? [],
      impersonating: !!d?.impersonating,
    };
  });
  useEffect(() => {
    return onAuthChange(() => {
      const d = getDecoded();
      setSnap({ roles: d?.roles ?? [], impersonating: !!d?.impersonating });
    });
  }, []);
  return snap;
}

function NavGroups() {
  const { roles, impersonating } = useRoleSnapshot();
  const isSuper = roles.includes('super_admin');
  const isOrgAdmin = roles.includes('org_admin');

  // super_admin without impersonation: only superadmin nav
  if (isSuper && !impersonating) {
    return (
      <Group titleKey="nav.superadmin" items={superAdminItems} startIndex={0} />
    );
  }

  // tenant nav. Hide admin group for non-org_admin (staff/technician).
  return (
    <>
      <Group titleKey="nav.main" items={mainItems} startIndex={0} />
      {isOrgAdmin && (
        <Group
          titleKey="nav.admin"
          items={adminItems}
          startIndex={mainItems.length}
        />
      )}
      {isOrgAdmin && (
        <Group
          titleKey="nav.data"
          items={dataItems}
          startIndex={mainItems.length + adminItems.length}
        />
      )}
    </>
  );
}

function SidebarContent() {
  const setSidebarOpen = useApp(s => s.setSidebarOpen);
  const companyName = useApp(s => s.settings.companyName);
  return (
    <>
      <div className="p-4 pb-2">
        <div className="relative flex items-center justify-between">
          <div className="relative overflow-hidden rounded-xl flame-gradient p-4 text-white glow-primary flex-1">
            <div className="absolute -right-4 -top-4 h-20 w-20 rounded-full bg-white/10 blur-xl" />
            <div className="absolute -bottom-6 -left-2 h-16 w-16 rounded-full bg-white/10 blur-xl" />
            <div className="relative flex items-center gap-2.5">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-white/20 backdrop-blur-sm ring-1 ring-white/30">
                <Flame className="h-5 w-5" strokeWidth={2.5} />
              </div>
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-wider opacity-90 flex items-center gap-1">
                  <Sparkles className="h-2.5 w-2.5" />
                  Fire Track
                </div>
                <div className="text-base font-bold leading-tight">
                  {companyName}
                </div>
              </div>
            </div>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="ml-2 flex h-11 w-11 items-center justify-center rounded-lg text-muted-foreground hover:bg-accent lg:hidden"
            aria-label="Close sidebar"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      <nav className="flex-1 px-3 py-2">
        <NavGroups />
      </nav>

      <InstallBanner />
      <SidebarFooter />
    </>
  );
}

export function Sidebar() {
  const sidebarOpen = useApp(s => s.sidebarOpen);
  const setSidebarOpen = useApp(s => s.setSidebarOpen);
  const location = useLocation();

  // Close sidebar on route change (mobile)
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname, setSidebarOpen]);

  return (
    <>
      {/* Desktop sidebar — always visible at lg+ */}
      <aside
        className="sticky top-0 h-screen w-[248px] shrink-0 border-r bg-card/50 backdrop-blur-sm overflow-y-auto flex-col hidden lg:flex"
        data-tour="sidebar"
      >
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
          className="flex flex-col h-full"
        >
          <SidebarContent />
        </motion.div>
      </aside>

      {/* Mobile sidebar — overlay drawer */}
      <AnimatePresence>
        {sidebarOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm lg:hidden"
              onClick={() => setSidebarOpen(false)}
            />
            <motion.aside
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ duration: 0.25, ease: 'easeOut' }}
              className="fixed inset-y-0 left-0 z-50 w-[280px] max-w-[85vw] bg-card border-r overflow-y-auto flex flex-col lg:hidden"
              data-tour="sidebar"
            >
              <SidebarContent />
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
